import { ConversationalConnectorService } from '@klant/conversational-connector/conversational-connector.service';
import { ConversationalChatVisibility, LoginStatus, Platform } from '@klant/conversational-connector/klant-conversational-connector.model';
import { ChatMessageEvent } from '@klant/conversational-connector/models/ChatMessage.model';
import { ConversationalConnectorAnalyticsService } from '@klant/conversational-connector/services/analytics/analytics.service';

import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { EMPTY } from 'rxjs';

import { ApContentService } from '@ap/content';
import { ApError } from '@ap/error';

import { ChatNotificationsModel } from '../../klant/conversational-connector/models/ChatNotifications.model';
import { SessionStorageService } from '../../session-storage/session-storage.service';
import { ChatSessionStore } from '../../signal-stores/chat-session-store/chat-session.store';
import { ConversationalChatStore } from '../../signal-stores/conversational-chat-store/conversational-chat.store';
import { ConversationalConfigStore } from '../../signal-stores/conversational-config-store/conversational-config.store';

import content from './mijn-klant-conversational.content.json';

@Component({
    selector: 'mijn-klant-conversational',
    templateUrl: 'mijn-klant-conversational.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MijnKlantConversationalComponent implements OnInit {
    private readonly apContentService = inject(ApContentService);
    private readonly chatSessionStore = inject(ChatSessionStore);
    private readonly conversationalChatStore = inject(ConversationalChatStore);
    private readonly conversationalConfigStore = inject(ConversationalConfigStore);
    private readonly conversationalConnectorAnalyticsService = inject(ConversationalConnectorAnalyticsService);
    private readonly conversationalConnectorService = inject(ConversationalConnectorService);
    private readonly elementRef = inject(ElementRef);
    private readonly sessionStorageService = inject(SessionStorageService);

    public readonly showError = computed(() => {
        return (
            (this.conversationalChatStore.takeoverError() ||
                this.conversationalConfigStore.errorGettingConfiguration() === true ||
                this.chatSessionStore.errorGettingChatSessionIdChat() === true ||
                this.isOperational() === false) &&
            this.doneLoading()
        );
    });
    public readonly showConnector = computed(() => !this.showError() && this.isOperational());
    public readonly takeoverError = computed(() => {
        return (
            this.conversationalChatStore.takeoverError() ??
            new ApError(this.apContentService.get('mijn.klant.conversational.takeover.error.message'), '', undefined, false)
        );
    });

    private readonly doneLoading = this.conversationalConfigStore.doneLoading;
    private readonly isOperational = this.conversationalConfigStore.isOperational;

    private readonly unreadChatMessagesCount = this.conversationalChatStore.unreadChatMessagesCount;
    private readonly shouldFetchChatSession = computed(() => {
        return (
            this.conversationalChatStore.chatVisibility() === ConversationalChatVisibility.OPEN &&
            this.isOperational() &&
            !this.chatSessionStore.chatSession()
        );
    });
    private readonly shouldFetchUnreadMessages = computed(
        () => this.conversationalChatStore.chatVisibility() === ConversationalChatVisibility.INVISIBLE && this.isOperational()
    );
    private readonly unreadMessagesResource = rxResource({
        params: () => this.shouldFetchUnreadMessages(),
        stream: ({ params }) => {
            if (params) {
                return this.conversationalConnectorService.getChatNotifications();
            }
            return EMPTY;
        }
    });
    private readonly unreadMessagesCount = computed(() => {
        if (this.unreadMessagesResource.hasValue()) {
            const chatNotifications = this.unreadMessagesResource.value();
            return this.calculateUnreadMessageCount(chatNotifications);
        }
        return;
    });

    @HostListener('document:chatMessage', ['$event' as string])
    public handleChatMessage(event: ChatMessageEvent) {
        this.conversationalChatStore.setFaqMessage(event.detail.chatMessage);
    }

    public constructor() {
        effect(() => {
            if (this.conversationalConfigStore.shouldTriggerErrorOnMijnContactPage()) {
                this.triggerErrorOnMijnContactPage();
            }
        });

        effect(() => {
            if (this.shouldFetchChatSession()) {
                this.fetchChatSessionId();
            }
        });

        effect(() => {
            const unreadMessagesCount = this.unreadMessagesCount();
            if (unreadMessagesCount) {
                this.conversationalChatStore.setUnreadChatMessagesCount(unreadMessagesCount);
            }
        });

        effect(() => {
            if (this.chatSessionStore.chatSession()) {
                this.chatSessionStore.resetUnreadMessageCounter();
            }
        });

        effect(() => {
            const hasUnreadMessages = this.unreadChatMessagesCount() > 0;

            if (hasUnreadMessages) {
                this.popUpChatBubble();
            }
        });
    }

    public ngOnInit(): void {
        this.apContentService.addContentItems(content);
        this.conversationalChatStore.setPlatform(Platform.MIJN);

        this.checkOpenChatQueryParam();
    }

    private checkOpenChatQueryParam(): void {
        const openChatParam = this.sessionStorageService.getOpenChatQueryParamValue();

        if (openChatParam) {
            this.conversationalChatStore.setChatVisibility(ConversationalChatVisibility.OPEN);
        }
    }

    private triggerErrorOnMijnContactPage(): void {
        const message = this.apContentService.get('mijn.klant.conversational.error.message');
        const status = 'error';

        const notificationEvent = new CustomEvent('chatNotification', {
            detail: { message, status }
        });
        this.elementRef.nativeElement.getRootNode().querySelector('mijn-klant-conversational-web')?.dispatchEvent(notificationEvent);
    }

    private fetchChatSessionId(): void {
        this.conversationalConnectorAnalyticsService.pushAnalyticsPageview(LoginStatus.LOGGED_IN);
        this.chatSessionStore.getChatSessionId();
    }

    private popUpChatBubble(): void {
        this.conversationalChatStore.setChatVisibility(ConversationalChatVisibility.MINIMIZED);
    }

    private calculateUnreadMessageCount(chatNotifications: ChatNotificationsModel): number {
        return chatNotifications.chatNotifications.reduce((sum, notification) => sum + notification.count, 0);
    }
}
